import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import {
  PrismBadge,
  PrismBanner,
  PrismButton,
  PrismCard,
  PrismInput,
  PrismLoadingBar,
  PrismMessage,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import perform from 'ember-concurrency/helpers/perform';
import { t } from 'ember-intl';
import { and, not, or } from 'ember-truth-helpers';
import GeneralButtonsOtbSelfServiceButton from 'lumen/components/general/buttons/otb-self-service-button';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralDismissibleBanner from 'lumen/components/general/dismissible-banner';
import GeneralDropdownsDropdownArrowButton from 'lumen/components/general/dropdowns/dropdown-arrow-button';
import GeneralDropdownsDropdownYield from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsTriggersTriggerDropdownBasic from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-basic';
import GeneralEmptyStateOtbBanner from 'lumen/components/general/empty-state-otb-banner';
import GeneralFilterBarDivider from 'lumen/components/general/filter-bar/divider';
import GeneralFilterBarFilterBar from 'lumen/components/general/filter-bar/filter-bar';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import GeneralMessagingBanner from 'lumen/components/general/messaging-banner';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import HotelShortTermRentalChartsChartShortTermRentalEvolutionDay from 'lumen/components/hotel/short-term-rental/charts/chart-short-term-rental-evolution-day';
import HotelShortTermRentalChartsChartShortTermRentalEvolutionRange from 'lumen/components/hotel/short-term-rental/charts/chart-short-term-rental-evolution-range';
import HotelShortTermRentalFixedKpiHeader from 'lumen/components/hotel/short-term-rental/fixed-kpi-header';
import HotelShortTermRentalShortTermRentalBreakdowns from 'lumen/components/hotel/short-term-rental/short-term-rental-breakdowns';
import HotelShortTermRentalShortTermRentalKpiList from 'lumen/components/hotel/short-term-rental/short-term-rental-kpi-list';
import HotelShortTermRentalShortTermRentalMap from 'lumen/components/hotel/short-term-rental/short-term-rental-map';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import type HotelShortTermRentalUpsellController from 'lumen/controllers/hotel/short-term-rental-upsell';
import hasFeature from 'lumen/helpers/has-feature';
import { FILTER_BAR_PAGETYPES } from 'lumen/utils/constants/general-constants';
import {
  OTB_SS_ENTRY_POINTS,
  OTB_SS_VIA_QP_VALUES,
} from 'lumen/utils/constants/otb-selfservice-constants';

interface HotelShortTermRentalUpsellSignature {
  Args: {
    controller: HotelShortTermRentalUpsellController;
    model: unknown;
  };
}

<template>
  {{! template-lint-disable require-input-label require-presentational-children }}
  <div
    class={{if
      @controller.shortTermRentalUpsell.isShortTermRentalUpsellAvailable
      "short-term-rental-upsell-nav-wrapper"
    }}
  >
    <GeneralPageHeader class="short-term-rental">
      <div class="content-wrapper ic-product-tour-rental-nav">
        <div class="flex gap-400">
          <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
          {{! @glint-expect-error: Incorrect type }}
          <GeneralDatepickersDatepickerWithSidebar
            @displayEvents={{false}}
            @maxDate={{@controller.datePickerMaxDate}}
            @maxRange="1 year"
            @minDate={{@controller.datePickerMinDate}}
            @range={{@controller.datePickerRange}}
            @rangeChanged={{@controller.rangeChanged}}
            @selectSingleDay={{false}}
            @sidebarLists={{@controller.datePickerRangeOptions}}
          />

          <div
            class="short-term-rental__ref-select-container ml-400 flex flex-row items-center"
          >
            <PrismSelectInput
              @onChange={{@controller.onRefDaysAgoSelectionChanged}}
              @options={{@controller.refDaysAgoSelectionOptions}}
              @value={{@controller.selectedRefDaysAgo}}
            />
          </div>

          <div class="mx-200 flex items-center">
            <GeneralFilterBarDivider />
          </div>

          <div class="range-picker-wrapper mr-200">
            <GeneralDropdownsDropdownYield
              @onCancel={{@controller.onCancel}}
              {{! @glint-expect-error: Incorrect type }}
              @selected={{@controller.formattedPriceRange}}
              @triggerComponent={{GeneralDropdownsTriggersTriggerDropdownBasic}}
              as |dropdown|
            >
              <section class="range-picker">
                <section class="range-picker__content mt-400 gap-400">
                  <PrismInput
                    @hasErrors={{@controller.isInvalidPriceRangeStart}}
                    @inlineLabel={{@controller.currencyCode}}
                    @label="{{t
                      'general.min_price'
                    }} ({{@controller.currencySymbol}}{{@controller.minPrice}}"
                    @onInput={{@controller.setPriceRangeStart}}
                    @type="number"
                    @value={{@controller.tempPriceRangeStart}}
                  />
                  <PrismInput
                    @hasErrors={{@controller.isInvalidPriceRangeEnd}}
                    @inlineLabel={{@controller.currencyCode}}
                    @label="{{t
                      'general.max_price'
                    }} ({{@controller.currencySymbol}}{{@controller.maxPrice}}"
                    @onInput={{@controller.setPriceRangeEnd}}
                    @type="number"
                    @value={{@controller.tempPriceRangeEnd}}
                  />
                </section>

                <section class="range-picker__footer">
                  <div class="save-cancel">
                    <GeneralFormFieldsCancelAndConfirm
                      @cancel={{fn @controller.cancelChanges dropdown}}
                      @confirm={{fn @controller.applyPriceChanges dropdown}}
                      @isConfirmDisabled={{or
                        @controller.isInvalidPriceRangeStart
                        @controller.isInvalidPriceRangeEnd
                      }}
                    />
                  </div>
                </section>
              </section>
            </GeneralDropdownsDropdownYield>
          </div>

          <div class="range-picker-wrapper mr-200">
            <GeneralDropdownsDropdownYield
              @onCancel={{@controller.onCancel}}
              {{! @glint-expect-error: Incorrect type }}
              @selected={{@controller.radiusFromHotelText}}
              @triggerComponent={{GeneralDropdownsTriggersTriggerDropdownBasic}}
              as |dropdown|
            >
              <section class="range-picker">
                <section class="range-picker__content">
                  <input
                    class="slider mr-300"
                    max={{@controller.maxRadius}}
                    min={{0}}
                    step={{1}}
                    type="range"
                    value={{@controller.tempRadius}}
                    {{on "input" @controller.updateRadius}}
                  />
                  <span>{{@controller.formattedTempRadius}}</span>
                </section>

                <section class="range-picker__footer">
                  <div class="save-cancel">
                    <GeneralFormFieldsCancelAndConfirm
                      @cancel={{fn @controller.cancelChanges dropdown}}
                      @confirm={{fn @controller.applyRadiusChanges dropdown}}
                    />
                  </div>
                </section>
              </section>
            </GeneralDropdownsDropdownYield>
          </div>

          <div class="filter-bar-wrapper">
            {{! @glint-expect-error: Incorrect type }}
            <GeneralFilterBarFilterBar
              @activeFilters={{@controller.mappedFilters}}
              @filterBarSegmentationClicked={{@controller.clearActiveFilter}}
              @hideFavourites={{true}}
              @isLoading={{false}}
              @pageType={{FILTER_BAR_PAGETYPES.RATE_INSIGHT}}
            />
            <div
              class="filter-button-container ml-100 flex flex-col justify-center"
            >
              {{#if @controller.hasEditRights}}
                <PrismButton
                  @buttonType="secondary"
                  @isDisabled={{or
                    @controller.hasOnlyDefaultFiltersSelected
                    @controller.hasInvalidSelection
                  }}
                  @label={{t "general.save"}}
                  @onClick={{@controller.setDefault}}
                  @size="small"
                  data-event-id="hotel_short_term_rental_filters-set_default_button-click"
                />
              {{/if}}
              <PrismButton
                @buttonType="secondary"
                @isDisabled={{@controller.isResetOrRemoveDisabled}}
                @label={{@controller.resetOrRemoveFiltersText}}
                @onClick={{@controller.resetOrRemoveFilters}}
                @size="small"
              />
            </div>
          </div>
        </div>
      </div>
    </GeneralPageHeader>

    <HotelShortTermRentalFixedKpiHeader
      @isLoading={{@controller.model.kpis.isRunning}}
      @kpis={{@controller.model.kpis.value}}
    />

    {{#if @controller.hasInvalidSelectionBanner}}
      {{! @glint-expect-error: Incorrect type }}
      <GeneralDismissibleBanner
        @bannerMessage={{t "short_term_rental.invalid_selection"}}
        @bannerStyle="warning"
        @closeBanner={{@controller.dismissBanner}}
        @intercomArticleLink="en/articles/7206570-short-term-rental-breakdown-invalid-filter-selection"
        @shouldShowIntercomLink={{true}}
      />
    {{/if}}
  </div>

  <GeneralPageMain>
    <div class="content-wrapper main-content">
      {{#if @controller.shortTermRentalUpsell.isShortTermRentalUpsellAvailable}}
        <div class="short-term-rental-upsell"></div>

        <GeneralMessagingBanner
          @alt="Binoculars icon"
          @image="100x100_binoculars"
          @subtitle={{t
            "short_term_rental.upsell.banner.message"
            properties=@controller.shortTermRentalUpsell.shortTermRentalProperties
            relevantProperties=@controller.shortTermRentalUpsell.shortTermRentalRelevantProperties
          }}
          @title={{t "short_term_rental.upsell.banner.title"}}
        >
          <div class="flex flex-row gap-200">
            <PrismButton
              @buttonType="secondary"
              @label={{t "upsell.no_thanks"}}
              @onClick={{perform
                @controller.shortTermRentalUpsell.requestDismissTrialTask
              }}
            />
            <PrismButton
              @buttonType="brand"
              @label={{t "upsell.start_trial_14_days"}}
              @onClick={{perform
                @controller.shortTermRentalUpsell.requestStartTrialTask
              }}
              data-event-id="hotel-short-term-rental-upsell-start-trial-button"
            />
          </div>
        </GeneralMessagingBanner>
      {{/if}}

      {{#if
        (and
          (not @controller.isBannerHidden)
          (not
            @controller.shortTermRentalUpsell.isShortTermRentalUpsellAvailable
          )
        )
      }}
        <PrismBanner
          @bannerMessage={{t "short_term_rental.banner.message"}}
          @bannerStyle="info"
          @bannerTitle={{t "short_term_rental.banner.title"}}
          @close={{@controller.closeShortTermRentalBanner}}
          @icon="short-term-rental"
          @link={{@controller.shortTermRentalLink}}
        />
      {{/if}}

      <div
        class="section-title text-neutral-emphasis flex items-center justify-between"
      >
        <h2>{{t "menu.market"}}
          <span class="text-300--regular">
            {{t "general.daysago" count=@controller.refDaysAgo}}
          </span>
        </h2>
      </div>

      <div class="market-section">
        <HotelShortTermRentalShortTermRentalKpiList
          @isLoading={{@controller.model.kpis.isRunning}}
          @kpis={{@controller.model.kpis.value}}
        />

        <div
          class="market-section__map
            {{if
              @controller.shortTermRentalUpsell.isShortTermRentalUpsellAvailable
              'market-section__upsell-map'
            }}"
        >
          {{#if @controller.isGoogleMapsAPIAvailable}}
            <HotelShortTermRentalShortTermRentalMap
              @events={{@controller.events}}
              @isLoading={{or
                @controller.model.map.isRunning
                @controller.model.evolutions.isRunning
              }}
              @mapData={{@controller.model.map.value}}
              @radius={{@controller.radiusWithFallback}}
              @singleDaySelected={{@controller.singleDaySelected}}
            />
          {{else}}
            <div class="google-maps-container flex items-center justify-center">
              <p class="text-300--regular">
                {{t "settings.competitors.maps_unavailable"}}
              </p>
            </div>
          {{/if}}
        </div>
      </div>

      <div class="section-title mt-800 flex items-center justify-between">
        <h2 class="text-neutral-emphasis mr-200 flex items-center">
          {{t "short_term_rental.cards.evolution"}}
        </h2>
        <div
          class="section-title-group date-aggregation-dropdown text-neutral-subdued ml-300"
        >
          <p class="text-300--regular mr-200">
            {{t "parity.view_by"}}
          </p>
          <PrismSelectInput
            @horizontalPosition="right"
            @onChange={{@controller.setDateAggregation}}
            @options={{@controller.dateAggregationOptions}}
            @value={{@controller.selectedDateAggregation}}
          />
        </div>
      </div>

      <PrismCard @contentLayout="noPadding" class="mb-800">
        <:header as |Header|>
          <div
            class="button--unstyled flex items-center justify-between"
            role="button"
            {{on "click" @controller.toggleEvolutionCard}}
          >
            <div class="flex flex-row items-center">
              {{#if @controller.singleDaySelected}}
                <Header.title class="mr-100">
                  {{t "general.pace"}}
                </Header.title>
                <PrismBadge @label={{@controller.formattedFromDate}} />
              {{else}}
                <Header.title class="mr-100">
                  {{t "market_insight.stay_date"}}
                </Header.title>
              {{/if}}
            </div>

            <div class="mr-100 flex items-center">
              <div class="mr-400">
                {{#if
                  (and
                    (not
                      @controller.myotbSs.isOtbSelfServiceUpsellBannerVisible
                    )
                    (hasFeature "otb.empty_placeholder")
                  )
                }}
                  <GeneralButtonsOtbSelfServiceButton
                    @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.RENTAL_EVOLUTION_CHART}}
                    @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.RATE_INSIGHT}}
                  />
                {{/if}}
              </div>

              <GeneralDropdownsDropdownArrowButton
                @isOpen={{@controller.isEvolutionCardOpen}}
              />
            </div>
          </div>
        </:header>

        <:content>
          {{#if @controller.isEvolutionCardOpen}}
            {{#if @controller.hasInvalidSelection}}
              <div class="flex items-center justify-center">
                <PrismMessage
                  @description={{t "short_term_rental.invalid_selection"}}
                  @illustration="100x100_critical"
                  @title={{t "general.no_data"}}
                />
              </div>
            {{else}}
              <GeneralEmptyStateOtbBanner
                @bannerPosition="top"
                @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.RENTAL_EVOLUTION_CHART}}
                @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.RATE_INSIGHT}}
              />

              <div class="evolution-chart-container mb-400">
                {{#if @controller.isEvolutionRequestLoading}}
                  <PrismLoadingBar class="rental-progress-bar" />
                {{else}}
                  {{#if @controller.singleDaySelected}}
                    {{! @glint-expect-error: Incorrect type }}
                    <HotelShortTermRentalChartsChartShortTermRentalEvolutionDay
                      @refDaysAgo={{@controller.refDaysAgo}}
                      @sortedData={{@controller.sortedData}}
                    />
                  {{else}}
                    <HotelShortTermRentalChartsChartShortTermRentalEvolutionRange
                      {{! @glint-expect-error: Incorrect type }}
                      @fromDate={{@controller.fromDateMoment}}
                      @refDaysAgo={{@controller.refDaysAgo}}
                      @selectedDateAggregation={{@controller.selectedDateAggregation.id}}
                      @sortedData={{@controller.sortedData}}
                    />
                  {{/if}}
                {{/if}}
              </div>
            {{/if}}
          {{/if}}
        </:content>
      </PrismCard>

      <div class="section-title mt-800 flex items-center justify-between">
        <h2 class="text-neutral-emphasis mr-200 flex items-center">
          {{t "revenue_insight.headers.segments"}}
        </h2>
      </div>

      <HotelShortTermRentalShortTermRentalBreakdowns
        @activeFilters={{@controller.activeFiltersWithFallback}}
        @addFilter={{@controller.addFilter}}
        @breakdowns={{@controller.model.breakdowns.value}}
        @hasInvalidSelection={{@controller.hasInvalidSelection}}
        @isLoading={{@controller.model.breakdowns.isRunning}}
      />
    </div>
  </GeneralPageMain>
</template> satisfies TOC<HotelShortTermRentalUpsellSignature>;
